import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import { Icon } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import Literal from 'components/Literal'; // FIXME Please use getLiteral here
import TextField from 'components/Fields/Text';
import SelectField from 'components/Fields/Select';
import Checkbox from 'components/Fields/Checkbox';
import { ErrorAdvice } from 'components/advices';
import { getError } from 'utils/Errors';
import Options from './Options';

import './index.scss';

const propTypes = {
    data: PropTypes.object,
    errors: PropTypes.object,
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
    moveItem: PropTypes.func.isRequired,
    findItem: PropTypes.func.isRequired,
    accountFields: PropTypes.array,
};

const getQuestionTypes = () => [
    {
        value: 'text',
        label: getLiteral('label_campaigns_dropdown_freetext'),
        defaults: {
            custom: false,
            items: [],
        },
    },
    {
        value: 'integer',
        label: getLiteral('label_campaigns_add_questions_number'),
        defaults: {
            custom: false,
            items: [],
        },
    },
    {
        value: 'date',
        label: getLiteral('label_campaigns_add_questions_date'),
        defaults: {
            custom: false,
            items: [],
        },
    },
    {
        value: 'singleValueList',
        label: getLiteral('label_campaigns_crud_questions_add_questions_dropdown'),
        config: true,
        configType: 'options',
        defaults: {
            custom: true,
            items: [{}],
        },
    },
    {
        value: 'multipleValueList',
        label: getLiteral('label_campaigns_crud_questions_add_questions_multiple_choice'),
        config: true,
        configType: 'options',
        defaults: {
            custom: true,
            items: [{}],
        },
    },
];

const Question = ({ data, errors, onChange, onDelete, moveItem, findItem, accountFields }) => {
    const questionTypes = getQuestionTypes();
    const [state, setState] = useState(data || {});
    const currentLinkedField = useRef(state.properties?.linkedEntityField);
    const currentType = useRef('text');
    const currentItems = useRef(state.items || []);
    const currentValueListName = useRef(state.properties?.valueListName);
    const ref = useRef(null);
    const dragHandleRef = useRef(null);

    const id = data.questionId || data._id;

    const [{ isDragging }, drag] = useDrag({
        type: 'question',
        item: () => {
            return {
                id,
                originalIndex: findItem(id).index,
            };
        },
        end: (item, monitor) => {
            const { id: droppedId, originalIndex } = item;
            const didDrop = monitor.didDrop();

            if (!didDrop) {
                moveItem(droppedId, originalIndex);
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [, drop] = useDrop({
        accept: 'question',
        canDrop: () => false,
        hover: (item) => {
            const draggedId = item.id;
            const overId = data.questionId || data._id;

            if (draggedId !== overId) {
                const { index: draggedIndex } = findItem(draggedId);
                const { index: overIndex } = findItem(overId);
                moveItem(draggedIndex, overIndex);
            }
        },
    });

    // Apply drop to the whole question box
    drop(ref);
    // Apply drag only to the drag handle
    drag(dragHandleRef);

    const handleChange = useCallback(
        (field, field2) => {
            return (value) => {
                let newState = state;
                if (field && field2 && field2 === 'type') {
                    let type = questionTypes.find((type) => type.value === value);
                    currentType.current = type.value;
                    let defaults =
                        type && type.defaults
                            ? JSON.parse(JSON.stringify(type.defaults)) || {}
                            : {};

                    newState = {
                        ...newState,
                        [field]: {
                            ...newState[field],
                            [field2]: value,
                            ...defaults,
                        },
                    };
                } else if (field && field2) {
                    newState = {
                        ...newState,
                        [field]: {
                            ...newState[field],
                            [field2]: value,
                        },
                    };
                } else if (field) {
                    newState = {
                        ...newState,
                        [field]: value,
                    };
                }
                setState(newState);
                onChange(newState);
            };
        },
        [state, onChange, questionTypes],
    );

    const onHandleTypeChange = useCallback(
        (index) => {
            return (value) => {
                let newState = {
                    ...state,
                    properties: {
                        ...state.properties,
                        items: [...state.properties.items],
                    },
                };
                newState.properties.items[index].value = value;
                currentItems.current = newState.properties.items;
                setState(newState);
                onChange(newState);
            };
        },
        [state, onChange],
    );

    const onHandleAddOption = useCallback(() => {
        let newState = {
            ...state,
            properties: {
                ...state.properties,
                items: [...state.properties.items],
            },
        };
        newState.properties.items.push({});
        setState(newState);
        onChange(newState);
    }, [state, onChange]);

    const onChangeAccountField = useCallback(
        (value) => {
            let fields = accountFields;
            let field = fields.find((f) => f.value === value).field;
            currentLinkedField.current = value;
            currentValueListName.current = field.valueListName;
            let newState = {
                ...state,
                properties: {
                    ...state.properties,
                    type: field.dataType,
                    custom: false,
                    valueListName: field.valueListName,
                    linkedEntityField: value,
                    linkedEntity: 'accounts',
                    items: [],
                },
            };
            setState(newState);
            onChange(newState);
        },
        [state, onChange, accountFields],
    );

    const onChangeLinked = useCallback(
        (value) => {
            let type = currentType.current ? currentType.current : state.properties.type;
            let custom = ['singleValueList', 'multipleValueList'].includes(type);
            let newState = {
                ...state,
                properties: {
                    ...state.properties,
                    custom: value ? false : custom,
                    linkedEntityField: value ? currentLinkedField.current : null,
                    linkedEntity: value ? 'accounts' : null,
                    valueListName: value ? currentValueListName.current : null,
                    items: value ? [] : currentItems.current,
                    type: type,
                },
            };
            setState(newState);
            onChange(newState);
        },
        [state, onChange],
    );

    const renderOptions = () => {
        let { properties } = state;
        return (
            <div className="options-list">
                <Options
                    onChange={onHandleTypeChange}
                    onHandleAddOption={onHandleAddOption}
                    options={
                        properties.items && properties.items.length > 0 ? properties.items : null
                    }
                />
            </div>
        );
    };

    const renderConfig = () => {
        let { properties } = state;

        if (properties && properties.type) {
            let questionType = questionTypes.find((type) => type.value === properties.type);

            if (!questionType || !questionType.config) return null;

            switch (questionType.configType) {
                case 'options':
                    return renderOptions();
                default:
                    return null;
            }
        }
    };

    const renderLinkedAccountField = () => {
        let { properties } = state;
        let showExtras = properties.linkedEntity;

        return (
            <div className="questions-linked-account-field">
                <div className="questions-linked-account-custom">
                    <Checkbox
                        label={
                            <Literal literal="label_campaigns_crud_questions_add_questions_linked_to_account_field" />
                        }
                        onChange={onChangeLinked}
                        value={!!properties.linkedEntity}
                    />
                </div>
                {showExtras && (
                    <SelectField
                        label={<Literal literal="Label_campaigns_add_questions_which" />}
                        hint={<Literal literal="Label_campaigns_add_questions_select_account" />}
                        onChange={onChangeAccountField}
                        options={accountFields}
                        description={getLiteral('Label_campaigns_add_questions_info_2')}
                        value={properties.linkedEntityField}
                        mandatory={true}
                        isClearable={false}
                    />
                )}
                {showExtras && (
                    <ErrorAdvice>
                        <Literal literal="Label_campaigns_add_questions_caution" />
                    </ErrorAdvice>
                )}
            </div>
        );
    };

    let { text, properties } = state;

    const classes = ['question-box'];
    if (isDragging) classes.push('question-box__dragging');

    return (
        <div ref={ref} className={classes.join(' ')}>
            <div ref={dragHandleRef} className="question-box-header">
                :::
            </div>
            <div className="question-box-content">
                <div className="fm-text-field-big">
                    <TextField
                        autoFocus={!text}
                        size="large"
                        onChange={handleChange('text')}
                        value={text || ''}
                        mandatory={true}
                        error={errors && getError(errors['text'])}
                        hint={getLiteral('label_campaigns_write_campaign_question')}
                    />
                </div>
                <div className="question-checkboxes">
                    <Checkbox
                        label={
                            <Literal literal="label_campaigns_crud_questions_add_questions_make_mandatory" />
                        }
                        onChange={handleChange('properties', 'mandatory')}
                        value={properties.mandatory}
                        description={getLiteral(
                            'label_campaigns_crud_questions_add_questions_make_mandatory_info',
                        )}
                    />
                    {renderLinkedAccountField()}
                </div>
                {!properties.linkedEntity && (
                    <div className="question-types">
                        <SelectField
                            label={
                                <Literal literal="label_campaigns_crud_questions_add_questions_type" />
                            }
                            onChange={handleChange('properties', 'type')}
                            options={questionTypes}
                            error={errors && !!errors['type']}
                            value={properties.type}
                            mandatory={true}
                            isClearable={false}
                        />
                    </div>
                )}
                {!properties.linkedEntity && renderConfig()}
                <div className="question-box-actions">
                    <div className="question-box__remove" onClick={onDelete}>
                        <Icon name="delete" />
                    </div>
                </div>
            </div>
        </div>
    );
};

Question.propTypes = propTypes;

export default Question;
